import { map, Observable, of } from 'rxjs';

// FIXME: Will be handled by WEBAPPPLAT-2008
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureFlagsIpcService } from '@shure/shared/angular/ipc-renderer/feature-feature-flags';

import { FeatureFlagsProvider } from './feature-flags-provider';

export class FeatureFlagsService<
	TFeatureFlagIds,
	FeatureFlags extends { id: TFeatureFlagIds; enabled?: boolean },
	ConfigType = object
> {
	public isLoading$ = this.dataProvider?.isLoading$ ?? of(false);
	public restartRequired$ = this.featureFlagsIpcService?.isRestartRequired$;

	constructor(
		private readonly featureFlags: readonly FeatureFlags[],
		private readonly featureFlagsIpcService?: FeatureFlagsIpcService,
		private dataProvider?: FeatureFlagsProvider<TFeatureFlagIds, FeatureFlags, ConfigType>
	) {}

	/**
	 * Checks if a specific feature flag is enabled based on its ID.
	 */
	public isFeatureEnabled$(id: string): Observable<boolean> {
		/* eslint-disable indent */
		return this.featureFlagsIpcService
			? this.featureFlagsIpcService.isFeatureEnabled$(id)
			: this.enabledFeatureFlags$().pipe(
					map((enabledFeatureFlags) => enabledFeatureFlags.some((flag) => flag.id === id))
			  );
		/* eslint-enable indent */
	}

	/**
	 * Retrieves an array of enabled feature flags.
	 */
	public enabledFeatureFlags$(): Observable<{ id: string; enabled: boolean }[]> {
		return this.featureFlagsIpcService
			? this.featureFlagsIpcService.listEnabledFeatures$()
			: of(<{ id: string; enabled: boolean }[]>this.featureFlags.filter((featureFlags) => featureFlags.enabled));
	}

	/**
	 * Checks if a specific feature flag is disabled based on its ID.
	 */
	public isFeatureDisabled$(id: string): Observable<boolean> {
		return this.featureFlagsIpcService
			? this.featureFlagsIpcService.isFeatureDisabled$(id)
			: this.isFeatureEnabled$(id).pipe(map((isEnabled) => !isEnabled));
	}

	/**
	 * Retrieves the status of a feature flag as an Observable boolean.
	 */
	public getFeatureFlag$(flagName: TFeatureFlagIds): Observable<boolean> {
		return this.dataProvider ? this.dataProvider.getFeatureFlag$(flagName) : of(false);
	}

	/**
	 * Retrieves the current configuration.
	 */
	public getConfig(): ConfigType {
		return this.dataProvider ? this.dataProvider.getConfig() : <ConfigType>{};
	}

	/**
	 * Sets the configuration as an Observable void.
	 */
	public setConfig$(config: ConfigType): Observable<void> {
		return this.dataProvider ? this.dataProvider.setConfig$(config) : of(undefined);
	}

	/**
	 * Refreshes the cache as an Observable void.
	 */
	public refreshCache$(): Observable<void> {
		return this.dataProvider ? this.dataProvider.refreshCache() : of(undefined);
	}

	public getDynamicConfig(flagName: TFeatureFlagIds): Observable<FeatureFlags> {
		return this.dataProvider ? this.dataProvider.getDynamicConfig$(flagName) : of(<FeatureFlags>{});
	}

	public restartForNewFeatures(): Promise<void> {
		return this.featureFlagsIpcService?.restartForNewFeatures() ?? Promise.resolve(undefined);
	}
}
